import { useState, useEffect, useCallback } from 'react';
import "./Form.css";

import submitContactForm from '../../../utilities/SubmitContactForm';
import { phoneNumberRegex, emailRegex } from '../../../utilities/FormUtilities';

import {
  useGoogleReCaptcha
} from 'react-google-recaptcha-v3';

import config from '../../../config';

interface iProps {
  comment?: string;
  onSubmitCallback?: Function;
}

const categories = [
  "Railcar Storage",
  "Transloading, Material Handling & Storage",
  "Find Property",
];

export default function ContactUsForm(props: iProps) {
  const [name, setName] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [email, setEmail] = useState('');
  const [selectedCategories, setSelectedCategories] = useState(new Set<string>());

  // Commented out to Match Watco teams layout suggestion
  // const [companyName, setCompanyName] = useState('');
  // const [states, setStates] = useState('');
  // const [additionalDetails, setAdditionalDetails] = useState(props.comment || '');

  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [reCaptchaToken, setReCaptchaToken] = useState<string>();
  const [responseMessage, setResponseMessage] = useState<string>();

  const { executeRecaptcha } = useGoogleReCaptcha();

  const handleReCaptchaVerify = useCallback(async () => {
    if (!executeRecaptcha) {
      console.warn('Execute recaptcha not yet available');
      return;
    }

    try {
      const token = await executeRecaptcha('ContactUs');
      return token;

    } catch (error) {
      throw new Error('ReCaptcha Error');
    }
  }, [executeRecaptcha]);

  useEffect(() => {
    handleReCaptchaVerify()
      .then(token => { if (token) setReCaptchaToken(token) })
      .catch(console.error);
  }, [handleReCaptchaVerify]);

  const onSubmit = async () => {
    setResponseMessage(undefined);

    const isValidPhoneNumber = phoneNumber.match(phoneNumberRegex);
    const isValidEmail = email.match(emailRegex);

    const errorMessages: string[] = [];

    if (!reCaptchaToken) {
      errorMessages.push("ReCaptcha failed.")
    }
    if (!name) {
      errorMessages.push("Name is required.")
    }
    if (!isValidPhoneNumber) {
      errorMessages.push("Valid Phone Number is required.");
    }
    if (!isValidEmail) {
      errorMessages.push("Valid Email is required.");
    }
    if (selectedCategories.size === 0) {
      errorMessages.push("Must select one category of interest.");
    }

    setValidationErrors(errorMessages);
    const validForm = errorMessages.length === 0;

    if (!validForm || !reCaptchaToken) return;

    const subject = 'GeoConnect - Contact Us'
    const body = `Name: ${name}\n`
      + `Phone Number: ${phoneNumber}\n`
      + `Email: ${email}\n`
      + `Interested in: ${Array.from(selectedCategories).join(", ")}\n`;

    submitContactForm(reCaptchaToken, config.contactUs.formId, subject, email, body).then(response => {
      response.json().then(json => {
        setResponseMessage(json.message);

        if (props.onSubmitCallback) {
          props.onSubmitCallback();
        }
      });
    }).catch((err) => {
      console.error(err);
      setResponseMessage("A network error occured. Please try again later.")
    });
  }

  const onCategoryCheckboxChange = (event: any) => {
    const categories = new Set(selectedCategories);
    if (event.target.checked) {
      categories.add(event.target.value);
    } else {
      categories.delete(event.target.value);
    }
    setSelectedCategories(categories);
  }

  return (
    <div className="contact-us-form">
      <div className="input-container">
        <input type='text'
          value={name}
          onChange={evt => {setName(evt.target.value)}}
          name='your-name'
          placeholder='*Name' />
      </div>
      <div className="input-container">
        <input type='text'
          value={phoneNumber}
          onChange={evt => {setPhoneNumber(evt.target.value)}}
          name='phone'
          placeholder='*Phone Number' />
      </div>
      <div className="input-container">
        <input type='text'
          value={email}
          onChange={evt => {setEmail(evt.target.value)}}
          name='e-mail'
          placeholder='*Email' />
      </div>
      <div className='input-container category-checkboxes'>
        <div className='label'>*I'm interested in (Check all that apply)</div>
        <div className='checkbox-container__wrapper'>
          {categories.map((option, index) =>
            <div key={index} className="checkbox-container">
              <input type="checkbox"
                id={`category-checkbox-${index}`}
                onChange={onCategoryCheckboxChange}
                checked={selectedCategories.has(option)}
                value={option}
              />
              <label htmlFor={`category-checkbox-${index}`}>{option}</label>
            </div>
          )}
        </div>
      </div>
      <div className='small-font'>
        * Required Fields
      </div>
      <div className='small-font'>
        This site is protected by reCAPTCHA and the Google&nbsp;
        <a href="https://policies.google.com/privacy">Privacy Policy</a> and&nbsp;
        <a href="https://policies.google.com/terms">Terms of Service</a> apply.
      </div>
      {validationErrors.map((msg, index) =>
        <div key={index} className='small-font color-orange'>
          {msg}
        </div>
      )}
      <div className="input-container button-container">
        <button className="bg-blue md-btn" onClick={onSubmit}>Submit</button>
      </div>
      {responseMessage &&
        <div>{responseMessage}</div>
      }
    </div>
  );
}